import api, { ApiResponse } from "../api";
import {
  ReleaseDetailsAttachmentModel,
  ReleaseDetailsModel,
  ReleaseDetailsSummaryModel
} from "./models";

const errorMessage = (response: ApiResponse<any>): string => {
  const error = response.getError?.();
  if (typeof error === "string" && error) {
    return error;
  }
  return "Something went wrong";
};

const unwrap = <T>(response: ApiResponse<T>): T => {
  if (response.ok()) {
    return response.data;
  }
  throw new Error(errorMessage(response));
};

class ReleaseDetailsService {
  private static instance: ReleaseDetailsService;
  private constructor() {}
  static getInstance(): ReleaseDetailsService {
    if (!ReleaseDetailsService.instance) {
      ReleaseDetailsService.instance = new ReleaseDetailsService();
    }
    return ReleaseDetailsService.instance;
  }

  public list = async (): Promise<ReleaseDetailsSummaryModel[]> => {
    const response = await api.get("/release-details");
    return unwrap<ReleaseDetailsSummaryModel[]>(response) || [];
  };

  public get = async (id: string): Promise<ReleaseDetailsModel> => {
    const response = await api.get(`/release-details/${id}`);
    return unwrap<ReleaseDetailsModel>(response);
  };

  public getForAnnouncement = async (announcementId: string): Promise<ReleaseDetailsModel | null> => {
    const response = await api.get(`/release-details/announcement/${announcementId}`);
    if (!response.ok()) {
      throw new Error(errorMessage(response));
    }
    if (response.status === 204 || !response.data) {
      return null;
    }
    return response.data;
  };

  public save = async (announcementId: string, body: string): Promise<ReleaseDetailsModel | null> => {
    const response = await api.put(`/release-details/announcement/${announcementId}`, { body });
    if (!response.ok()) {
      throw new Error(errorMessage(response));
    }
    if (response.status === 204 || !response.data) {
      return null;
    }
    return response.data;
  };

  public uploadAttachment = async (announcementId: string, file: File): Promise<ReleaseDetailsAttachmentModel> => {
    const formData = new FormData();
    formData.append("file", file);
    const response = await api.post(`/release-details/announcement/${announcementId}/attachments`, formData);
    return unwrap<ReleaseDetailsAttachmentModel>(response);
  };

  public deleteAttachment = async (attachmentId: string): Promise<void> => {
    const response = await api.delete(`/release-details/attachments/${attachmentId}`);
    if (!response.ok()) {
      throw new Error(errorMessage(response));
    }
  };

  public uploadImage = async (file: File): Promise<{ uri?: string }> => {
    const formData = new FormData();
    formData.append("file", file);
    const response = await api.post("/release-details/images", formData);
    return unwrap<{ uri?: string }>(response);
  };

  public downloadAttachment = async (attachmentId: string): Promise<Blob> => {
    const response = await api.get(`/release-details/attachments/${attachmentId}/download`, { responseType: "blob" });
    if (!response.ok()) {
      throw new Error("Unable to download the file");
    }
    return response.data;
  };
}

export default ReleaseDetailsService.getInstance();
